import { useMemo } from "react";
import { useSelector } from "store/hooks";
import { getAssistanceOptions } from "utils/assistanceOptions";
import { getHullDeductibleOptions } from "utils/hullDeductibleOptions";
import { getMtplInsuranceLimitOptions } from "utils/mtplInsuranceLimits";
import { LineOptionsGetters } from "../FinanceTab/FinanceForm/requestedLinesHelpers";

/**
 * CZ client: per line, a function giving the options the client can pick for
 * the line requested from a coverage start ("YYYY-MM-DD"): the MTPL insurance
 * limits ("{personal injury}/{property damage}") of the fleet's main MTPL
 * insurer and the hull deductible options ("{percent}%/min: {min} CZK/max:
 * {max} CZK") of the fleet's main Hull insurer applicable on that day.
 */
export const useClientLineOptions = (): LineOptionsGetters => {
  const fleet = useSelector((s) => s.vehicle.fleet);
  const mainMtplInsurerPartnerNumber = fleet?.mainMtplInsurerPartnerNumber;
  const mainHullInsurerPartnerNumber = fleet?.mainHullInsurerPartnerNumber;
  const fleetDefaultSettings = useSelector(
    (s) => s.vehicle.fleetDefaultSettings,
  );
  return useMemo(
    () => ({
      mtpl: (coverageFrom?: string | null) =>
        getMtplInsuranceLimitOptions(
          fleetDefaultSettings,
          mainMtplInsurerPartnerNumber,
          coverageFrom,
        ),
      hull: (coverageFrom?: string | null) =>
        getHullDeductibleOptions(
          fleetDefaultSettings,
          mainHullInsurerPartnerNumber,
          coverageFrom,
        ),
    }),
    [
      fleetDefaultSettings,
      mainMtplInsurerPartnerNumber,
      mainHullInsurerPartnerNumber,
    ],
  );
};

/**
 * CZ client: per line, a function giving the assistance options of the line's
 * main insurer (MTPL: main MTPL insurer, Hull: main Hull insurer) applicable
 * on a coverage start ("YYYY-MM-DD").
 */
export const useClientAssistanceOptions = (): LineOptionsGetters => {
  const fleet = useSelector((s) => s.vehicle.fleet);
  const mainMtplInsurerPartnerNumber = fleet?.mainMtplInsurerPartnerNumber;
  const mainHullInsurerPartnerNumber = fleet?.mainHullInsurerPartnerNumber;
  const fleetDefaultSettings = useSelector(
    (s) => s.vehicle.fleetDefaultSettings,
  );
  return useMemo(
    () => ({
      mtpl: (coverageFrom?: string | null) =>
        getAssistanceOptions(
          fleetDefaultSettings,
          mainMtplInsurerPartnerNumber,
          coverageFrom,
        ),
      hull: (coverageFrom?: string | null) =>
        getAssistanceOptions(
          fleetDefaultSettings,
          mainHullInsurerPartnerNumber,
          coverageFrom,
        ),
    }),
    [
      fleetDefaultSettings,
      mainMtplInsurerPartnerNumber,
      mainHullInsurerPartnerNumber,
    ],
  );
};
